import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { SignalWifiOff, Wifi } from "@mui/icons-material";
import ClientEnvironmentIcons from "../../../components/ClientEnvironmentIcons";
import TeamPresenceTabBreakdown from "../../../components/TeamPresenceTabBreakdown";
import { summarizePresenceClientEnvironments } from "../../../utils/clientEnvironment";
import { initialsFromName } from "./usePlatformConsoleOnlinePresence";

/**
 * Card de operador online no console da plataforma (presença).
 */
export default function PlatformPresenceUserCard({
  row,
  onlineCardSurface,
  tLive,
  screenTitleForPath,
  livePresenceNowMs,
  compact = false,
}) {
  const isStale = row.presenceStale === true;
  const tabs = row.tabs > 0 ? row.tabs : 0;
  const clientEnv = useMemo(
    () => summarizePresenceClientEnvironments(row.presence_detail),
    [row.presence_detail]
  );

  const x4TabCount = row.presence_detail?.xpro4Tabs?.length ?? 0;
  const singleX4Tab = x4TabCount === 1;
  const showHeaderEnv = singleX4Tab || x4TabCount === 0;

  const avatarSize = compact ? 40 : 44;
  const contentPx = compact ? 1.25 : 1.75;
  const contentPy = compact ? 1.25 : 1.5;

  const accentColor = isStale ? "text.disabled" : onlineCardSurface.accent;
  const surfaceBg = isStale
    ? (th) => alpha(th.palette.action.disabledBackground, th.palette.mode === "dark" ? 0.35 : 0.55)
    : onlineCardSurface.bgcolor;
  const surfaceBorder = isStale
    ? (th) => alpha(th.palette.divider, 0.9)
    : onlineCardSurface.borderColor;

  const cardInner = (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="flex-start"
      sx={{
        minWidth: 0,
        opacity: isStale ? 0.72 : 1,
        filter: isStale ? "grayscale(0.35)" : "none",
        transition: "opacity 0.35s ease, filter 0.35s ease",
      }}
    >
      <Avatar
        sx={{
          width: avatarSize,
          height: avatarSize,
          fontSize: compact ? "0.85rem" : "0.9rem",
          fontWeight: 700,
          border: 2,
          borderColor: accentColor,
          color: isStale ? "text.disabled" : "text.primary",
          bgcolor: (th) => alpha(th.palette.background.paper, isStale ? 0.5 : 0.85),
          flexShrink: 0,
          mt: compact ? 0 : 0.125,
        }}
      >
        {initialsFromName(row.nome)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.875 }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant={compact ? "body2" : "subtitle2"}
              fontWeight={700}
              noWrap
              title={row.nome}
              color={isStale ? "text.secondary" : "text.primary"}
              sx={{ lineHeight: 1.3 }}
            >
              {row.nome || "—"}
            </Typography>
            {!compact ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={row.workspace_host}
                sx={{ display: "block", mt: 0.25, lineHeight: 1.35 }}
              >
                {row.workspace_host || "—"}
              </Typography>
            ) : null}
          </Box>
          {isStale ? (
            <Tooltip title={tLive("dashboardPage.live.teamPresenceStaleTooltip")}>
              <Chip
                size="small"
                icon={<SignalWifiOff sx={{ "&&": { fontSize: compact ? 14 : 16 } }} />}
                label={tLive("dashboardPage.live.teamPresenceStaleBadge")}
                variant="outlined"
                sx={{
                  cursor: "help",
                  fontWeight: 600,
                  flexShrink: 0,
                  height: compact ? 26 : 28,
                  color: "text.secondary",
                  borderColor: "divider",
                  bgcolor: (th) => alpha(th.palette.action.disabledBackground, 0.4),
                  "& .MuiChip-label": { px: 0.65, py: 0 },
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title={tLive("dashboardPage.live.onlinePresenceTooltip")}>
              <Chip
                size="small"
                icon={<Wifi sx={{ "&&": { fontSize: compact ? 14 : 16 } }} />}
                label={tLive("dashboardPage.live.onlineNow")}
                color="success"
                variant="outlined"
                sx={{
                  cursor: "help",
                  fontWeight: 600,
                  flexShrink: 0,
                  height: compact ? 26 : 28,
                  borderColor: onlineCardSurface.accent,
                  bgcolor: (th) => alpha(th.palette.background.paper, th.palette.mode === "dark" ? 0.2 : 0.65),
                  "& .MuiChip-label": { px: 0.65, py: 0 },
                }}
              />
            </Tooltip>
          )}
        </Stack>

        {showHeaderEnv ? (
          <Box sx={{ minWidth: 0, pointerEvents: isStale ? "none" : "auto" }}>
            <ClientEnvironmentIcons
              deviceTypes={clientEnv.deviceTypes}
              browserNames={clientEnv.browserNames}
              appVersions={clientEnv.appVersions}
              appThemes={clientEnv.appThemes}
              size="small"
            />
          </Box>
        ) : null}

        <TeamPresenceTabBreakdown
          show={tabs > 0}
          socketTabs={tabs}
          presenceDetail={row.presence_detail}
          tLive={tLive}
          screenTitleForPath={screenTitleForPath}
          livePresenceNowMs={isStale ? row.presenceLastLiveAt || livePresenceNowMs : livePresenceNowMs}
          density={compact ? "dense" : "default"}
          showTabEnvIcons={!showHeaderEnv}
          compactSingleTab={singleX4Tab}
        />
      </Box>
    </Stack>
  );

  const shellSx = {
    pointerEvents: isStale ? "none" : "auto",
    borderLeftColor: accentColor,
    bgcolor: surfaceBg,
    borderColor: surfaceBorder,
  };

  if (compact) {
    return (
      <Box
        aria-disabled={isStale || undefined}
        sx={{
          p: contentPx,
          borderRadius: 1,
          border: 1,
          borderLeftWidth: 3,
          borderLeftStyle: "solid",
          ...shellSx,
        }}
      >
        {cardInner}
      </Box>
    );
  }

  return (
    <Card
      variant="outlined"
      aria-disabled={isStale || undefined}
      sx={{
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        borderLeftWidth: 4,
        borderLeftStyle: "solid",
        boxShadow: isStale
          ? "none"
          : (th) =>
              th.palette.mode === "dark"
                ? `0 1px 4px ${alpha("#000", 0.25)}`
                : `0 1px 2px ${alpha(th.palette.common.black, 0.06)}`,
        ...shellSx,
      }}
    >
      <CardContent
        sx={{
          px: contentPx,
          py: contentPy,
          "&:last-child": { pb: contentPy },
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {cardInner}
      </CardContent>
    </Card>
  );
}
